/**
 * Kinn Kleanbowl Estimator - Results Display System
 * Generates dynamic HTML tables and results display for Shopify
 */

class EstimatorResultsRenderer {
    constructor(estimator) {
        this.estimator = estimator;
        this.themeDirectory = '{{ "" | asset_url }}'; // Shopify asset URL
    }

    /**
     * Get the theme directory path for images
     */
    getThemeDirectory() {
        // Shopify environment - images served from assets
        return '';
    }

    /**
     * Render complete results section
     */
    renderResults(calculationResults) {
        const { inputs, feeding, hydration, combined, kleanCleaningHoursPerDay, sustainability } = calculationResults;
        
        const resultsHtml = `
            <div id="estimator-results-wrapper" class="estimator-results">
                ${this.renderTopImage(inputs.savingsFor)}
                ${this.renderMainTable(calculationResults)}
                ${this.renderMiddleSection(calculationResults)}
                ${this.renderSustainabilityTable(sustainability)}
                ${this.renderBottomSection()}
            </div>
        `;

        return resultsHtml;
    }

    /**
     * Render top image based on savings type
     */
    renderTopImage(savingsFor) {
        let imagePath;
        
        // Get image URLs from global configuration (set by Liquid template)
        if (window.estimatorConfig && window.estimatorConfig.images) {
            switch(savingsFor) {
                case 'feeding':
                    imagePath = window.estimatorConfig.images.feeding;
                    break;
                case 'hydration':
                    imagePath = window.estimatorConfig.images.hydration;
                    break;
                default:
                    imagePath = window.estimatorConfig.images.combined;
            }
        } else {
            // Fallback to placeholder images if config not available
            switch(savingsFor) {
                case 'feeding':
                    imagePath = '/assets/estimator-feeding-top.svg';
                    break;
                case 'hydration':
                    imagePath = '/assets/estimator-hydration-top.svg';
                    break;
                default:
                    imagePath = '/assets/estimator-combined-top.svg';
            }
        }

        return `
            <div class="estimator-results-top">
                <img src="${imagePath}" alt="Estimator Results" class="img-fluid" />
            </div>
        `;
    }

    /**
     * Render main results table
     */
    renderMainTable(results) {
        const { inputs, feeding, hydration, combined, kleanCleaningHoursPerDay } = results;

        if (inputs.savingsFor === 'both') {
            return this.renderBothTable(feeding, hydration, combined, kleanCleaningHoursPerDay);
        } else {
            return this.renderSingleTable(results);
        }
    }

    /**
     * Render table for "both" feeding and hydration
     */
    renderBothTable(feeding, hydration, combined, kleanCleaningHoursPerDay) {
        return `
            <div class="estimator-results-table-one">
                <div class="table-responsive">
                    <table class="table table-bordered">
                        <thead class="table-primary">
                            <tr>
                                <th></th>
                                <th>Daily Labor Hours Cleaning</th>
                                <th>Annual Labor & Material Cost</th>
                                <th>Annual Savings $</th>
                                <th>Annual Savings %</th>
                                <th>Investment Payback Period in Days</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr><td><strong>Scenario A:</strong></td><td></td><td></td><td></td><td></td><td></td></tr>
                            <tr>
                                <td>Paper Trays for Feeding</td>
                                <td>${this.estimator.formatNumber(feeding.tray.cleaningHours)}</td>
                                <td>${this.estimator.formatCurrency(feeding.tray.cost)}</td>
                                <td>${this.estimator.formatCurrency(feeding.tray.savingsDollars)}</td>
                                <td>${Math.round(feeding.tray.savingsPercent)}%</td>
                                <td>${feeding.tray.paybackDays} days</td>
                            </tr>
                            <tr>
                                <td>Regular Bowls for Water</td>
                                <td>${this.estimator.formatNumber(hydration.bowl.cleaningStaffHours)}</td>
                                <td>${this.estimator.formatCurrency(hydration.bowl.cost)}</td>
                                <td>${this.estimator.formatCurrency(hydration.bowl.savingsDollars)}</td>
                                <td>${Math.round(hydration.bowl.savingsPercent)}%</td>
                                <td>${hydration.bowl.paybackDays} days</td>
                            </tr>
                            <tr>
                                <td>Kinn Kleanbowl</td>
                                <td>${this.estimator.formatNumber(kleanCleaningHoursPerDay * 2)}</td>
                                <td>${this.estimator.formatCurrency(combined.totalKleanCost)}</td>
                                <td></td><td></td><td></td>
                            </tr>
                            <tr class="combined-row">
                                <td>Combined</td>
                                <td></td><td></td>
                                <td>${this.estimator.formatCurrency(combined.totalSavingsScenarioA)}</td>
                                <td>${Math.round((combined.totalSavingsScenarioA / combined.totalCostScenarioA) * 100)}%</td>
                                <td>${combined.paybackScenarioA} days</td>
                            </tr>
                            <tr><td colspan="6">&nbsp;</td></tr>
                            <tr><td><strong>Scenario B:</strong></td><td></td><td></td><td></td><td></td><td></td></tr>
                            <tr>
                                <td>Regular Bowls for Feeding</td>
                                <td>${this.estimator.formatNumber(feeding.bowl.cleaningStaffHours)}</td>
                                <td>${this.estimator.formatCurrency(feeding.bowl.cost)}</td>
                                <td>${this.estimator.formatCurrency(feeding.bowl.savingsDollars)}</td>
                                <td>${Math.round(feeding.bowl.savingsPercent)}%</td>
                                <td>${feeding.bowl.paybackDays} days</td>
                            </tr>
                            <tr>
                                <td>Regular Bowls for Water</td>
                                <td>${this.estimator.formatNumber(hydration.bowl.cleaningStaffHours)}</td>
                                <td>${this.estimator.formatCurrency(hydration.bowl.cost)}</td>
                                <td>${this.estimator.formatCurrency(hydration.bowl.savingsDollars)}</td>
                                <td>${Math.round(hydration.bowl.savingsPercent)}%</td>
                                <td>${hydration.bowl.paybackDays} days</td>
                            </tr>
                            <tr>
                                <td>Kinn Kleanbowl</td>
                                <td>${this.estimator.formatNumber(kleanCleaningHoursPerDay * 2)}</td>
                                <td>${this.estimator.formatCurrency(combined.totalKleanCost)}</td>
                                <td></td><td></td><td></td>
                            </tr>
                            <tr class="combined-row">
                                <td>Combined</td>
                                <td></td><td></td>
                                <td>${this.estimator.formatCurrency(combined.totalSavingsScenarioB)}</td>
                                <td>${Math.round((combined.totalSavingsScenarioB / combined.totalCostScenarioB) * 100)}%</td>
                                <td>${combined.paybackScenarioB} days</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        `;
    }

    /**
     * Render table for single option (feeding or hydration only)
     */
    renderSingleTable(results) {
        const { inputs, feeding, hydration, kleanCleaningHoursPerDay } = results;
        const isFeeding = inputs.savingsFor === 'feeding';
        const data = isFeeding ? feeding : hydration;

        let tableRows = '';
        if (isFeeding) {
            tableRows = `
                <tr>
                    <td>Paper Trays</td>
                    <td>${this.estimator.formatNumber(data.tray.cleaningHours)}</td>
                    <td>${this.estimator.formatCurrency(data.tray.cost)}</td>
                    <td class="text-primary fw-bold">${this.estimator.formatCurrency(data.tray.savingsDollars)}</td>
                    <td class="text-primary fw-bold">${Math.round(data.tray.savingsPercent)}%</td>
                </tr>
                <tr>
                    <td>Regular Bowls</td>
                    <td>${this.estimator.formatNumber(data.bowl.cleaningStaffHours)}</td>
                    <td>${this.estimator.formatCurrency(data.bowl.cost)}</td>
                    <td class="text-primary fw-bold">${this.estimator.formatCurrency(data.bowl.savingsDollars)}</td>
                    <td class="text-primary fw-bold">${Math.round(data.bowl.savingsPercent)}%</td>
                </tr>
            `;
        } else {
            tableRows = `
                <tr>
                    <td>Regular Bowls</td>
                    <td>${this.estimator.formatNumber(data.bowl.cleaningStaffHours)}</td>
                    <td>${this.estimator.formatCurrency(data.bowl.cost)}</td>
                    <td class="text-primary fw-bold">${this.estimator.formatCurrency(data.bowl.savingsDollars)}</td>
                    <td class="text-primary fw-bold">${Math.round(data.bowl.savingsPercent)}%</td>
                </tr>
            `;
        }

        const paybackRows = isFeeding ? `
            <tr>
                <td>Paper Trays</td>
                <td>${data.tray.paybackDays} days</td>
            </tr>
            <tr>
                <td>Regular Bowls</td>
                <td>${data.bowl.paybackDays} days</td>
            </tr>
        ` : `
            <tr>
                <td>Regular Bowls</td>
                <td>${data.bowl.paybackDays} days</td>
            </tr>
        `;

        return `
            <div class="estimator-results-table-one">
                <div class="table-responsive">
                    <table class="table table-bordered">
                        <thead class="table-primary">
                            <tr>
                                <th>System Used</th>
                                <th>Daily Labor Hours Cleaning</th>
                                <th>Annual Labor & Material Cost</th>
                                <th>Annual Savings $</th>
                                <th>Annual Savings %</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${tableRows}
                            <tr>
                                <td>Kinn Kleanbowl</td>
                                <td>${this.estimator.formatNumber(kleanCleaningHoursPerDay)}</td>
                                <td>${this.estimator.formatCurrency(data.klean.cost)}</td>
                                <td></td>
                                <td></td>
                            </tr>
                            <tr><td colspan="5">&nbsp;</td></tr>
                            <tr>
                                <td colspan="3"><strong>Investment Payback Period:</strong></td>
                                ${paybackRows}
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        `;
    }

    /**
     * Render middle section with debug info and image
     */
    renderMiddleSection(results) {
        const debugInfo = this.renderDebugInfo(results);
        
        const middleImageUrl = (window.estimatorConfig && window.estimatorConfig.images) 
            ? window.estimatorConfig.images.resultsMiddle 
            : '/assets/estimator-results-middle.svg';
            
        return `
            <div class="estimator-results-middle my-4">
                ${debugInfo}
                <div class="text-center">
                    <img src="${middleImageUrl}" alt="Results" class="img-fluid">
                </div>
            </div>
        `;
    }

    /**
     * Render debug information (shown in development)
     */
    renderDebugInfo(results) {
        // Only show debug in development or when explicitly requested
        if (window.location.search.includes('show_debug=1') || 
            (window.Shopify && window.Shopify.designMode)) {
            
            const { feeding, hydration } = results;
            const debugData = {
                feeding_klean_cost: feeding.klean.cost,
                hydration_klean_cost: hydration.klean.cost,
                combined_total: feeding.klean.cost + hydration.klean.cost
            };

            return `
                <div class="alert alert-info">
                    <h5>Debug Information</h5>
                    <pre class="small">${JSON.stringify(debugData, null, 2)}</pre>
                </div>
            `;
        }
        return '';
    }

    /**
     * Render sustainability metrics table
     */
    renderSustainabilityTable(sustainability) {
        return `
            <div class="estimator-results-table-two">
                <div class="sustainability-section p-4 rounded">
                    <div class="table-responsive">
                        <table class="table table-bordered" style="width: 100%">
                            <thead>
                                <tr>
                                    <th></th>
                                    <th>Kinn Kleanbowl</th>
                                    <th>Regular Bowls</th>
                                    <th>% Reduction</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>GHG - Green House Gases (lb)</td>
                                    <td>${this.estimator.formatNumber(sustainability.ghg.klean, 0)}</td>
                                    <td>${this.estimator.formatNumber(sustainability.ghg.regular, 0)}</td>
                                    <td class="reduction-cell">${sustainability.ghg.reduction}%</td>
                                </tr>
                                <tr>
                                    <td>Energy - Electricity (kWH)</td>
                                    <td>${this.estimator.formatNumber(sustainability.energy.klean, 0)}</td>
                                    <td>${this.estimator.formatNumber(sustainability.energy.regular, 0)}</td>
                                    <td class="reduction-cell">${sustainability.energy.reduction}%</td>
                                </tr>
                                <tr>
                                    <td>Water (gal)</td>
                                    <td>${this.estimator.formatNumber(sustainability.water.klean, 0)}</td>
                                    <td>${this.estimator.formatNumber(sustainability.water.regular, 0)}</td>
                                    <td class="reduction-cell">${sustainability.water.reduction}%</td>
                                </tr>
                                <tr>
                                    <td>Waste (lbs)</td>
                                    <td>(${this.estimator.formatNumber(Math.abs(sustainability.waste.klean), 0)})</td>
                                    <td>(${this.estimator.formatNumber(Math.abs(sustainability.waste.regular), 0)})</td>
                                    <td class="reduction-cell">${this.estimator.formatNumber(sustainability.waste.reduction, 0)}%</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        `;
    }

    /**
     * Render bottom section with disclaimer
     */
    renderBottomSection() {
        return `
            <div class="estimator-results-bottom">
                <div class="disclaimer p-4 rounded">
                    <small>
                        <strong>Cost Savings Sources:</strong> All cost estimates are based on actual US pet professional customer input and/or other industry sources. Note that these costs are estimates, may vary, and your business actual savings may be different.<br><br>
                        <strong>Sustainability Source:</strong> Life-Cycle Analysis (LCA) of a standard industry stainless steel bowl vs. a Kinn Kleanbowl conducted by Sterling Consulting in partnership with Pet Sustainability Coalition.<br><br>
                        Frequency of home washing from the FDA Center for Veterinary Medicine over the lifetime of your pet.<br>
                        * Environmental Impacts from Sourcing Product to Product End-of-Life (approx 10 years)<br>
                        ** Recycling Kleanbowl sugar cane fiber Refills creates negative waste by saving energy and greenhouse gases (GHG) as a replacement for virgin materials<br>
                        <sup>1</sup>Sterling Sustainability Consulting in collaboration with Pet Sustainability Coalition
                    </small>
                </div>
            </div>
        `;
    }

    /**
     * Show results with smooth animation
     */
    showResults(calculationResults, containerId = 'estimator-form-container') {
        const container = document.getElementById(containerId);
        if (!container) {
            console.error('Results container not found');
            return;
        }

        const resultsHtml = this.renderResults(calculationResults);
        
        // Find or create results container
        let resultsContainer = document.getElementById('estimator-results-container');
        if (!resultsContainer) {
            resultsContainer = document.createElement('div');
            resultsContainer.id = 'estimator-results-container';
            container.parentNode.insertBefore(resultsContainer, container.nextSibling);
        }
        
        // Smooth transition
        resultsContainer.style.opacity = '0';
        resultsContainer.innerHTML = resultsHtml;
        
        setTimeout(() => {
            resultsContainer.style.opacity = '1';
            
            // Scroll to results
            resultsContainer.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    }

    /**
     * Hide results
     */
    hideResults() {
        const resultsContainer = document.getElementById('estimator-results-container');
        if (resultsContainer) {
            resultsContainer.innerHTML = '';
        }
    }
}

// Export for use
window.EstimatorResultsRenderer = EstimatorResultsRenderer;